import style from "./style.scss";
import editorialStyle from "../../../components/editorial/style.scss";

import { Fragment } from "preact";
import Link, {
  Button,
  ButtonList,
  StickyButtons,
  ButtonDualGroup,
  ButtonGroup,
  affiliateMerchants,
} from "../../../components/link";
import AffiliateDisclosure from "../../../components/affiliate-disclosure";
import Center from "../../../components/center";
import Editorial from "../../../components/editorial";
import FixedTable from "../../../components/fixed-table";
import Head from "../../../components/head";
import Hero from "../../../components/hero";
import NoWrap from "../../../components/no-wrap";
import Tags from "../../../components/tags";
import ImageSet from "../../../components/image-set";
import EcoFlowCouponCode from "../components/coupon-code";
import Image from "../../../components/image";
import ReviewItem from "../../../components/review-item";
import Author from "../../../components/author";
import Accordion from "../../../components/accordion";
import YouTube from "../../../components/youtube";
import Warning from "../../../components/warning";
import SlugHeading from "../../../components/slug-heading";
import PhotoListItem from "../../../components/photo-list-item";
import SlugLink from "../../../components/slug-link";
import Questions from "../../../components/questions";
import namedRoutes from "../../../settings/routes";
import { lookupByUrl } from "../../../settings/route-data-lookup";

const headerInfo = lookupByUrl(namedRoutes.ecoflow.altCharger.transit);

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      positionY={67}
      background="/images/r/ecoflow-alternator-chargers/transit-ecoflow.webp"
      titlePrefix="EcoFlow"
      title="Alternator Charger"
      subTitle="Ford Transit Install Guide"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <p>
          <strong>
            This guide provides instructions for installing an EcoFlow
            Alternator Charger in your Ford Transit.
          </strong>{" "}
          The Transit's electrical system has unique features (BMS, CCPs,
          potential dual alternators/batteries) that require careful
          consideration.
        </p>

        <p>This guide is based on:</p>
        <ul>
          <li>Direct consultation with a Ford Pro BBAS Services Engineer</li>
          <li>Ford Body and Equipment Mounting Manual (BEMM)</li>
          <li>EcoFlow product manuals and specifications</li>
          <li>
            My experience installing both EcoFlow Alternator Charger models in a
            Ford Transit
          </li>
        </ul>

        <AffiliateDisclosure />

        <Accordion
          left
          items={[
            {
              heading: "Table of Contents",
              content: (
                <Fragment>
                  <ol>
                    <li>
                      <SlugLink>Connection Options</SlugLink>
                    </li>
                    <li>
                      <SlugLink>Transit Electrical System</SlugLink>
                      <ol class="upper-alpha">
                        <li>
                          <SlugLink>Alternator</SlugLink>
                        </li>
                        <li>
                          <SlugLink>Vehicle Battery</SlugLink>
                          <ol class="lower-roman">
                            <li>
                              <SlugLink>
                                Battery Management System (BMS)
                              </SlugLink>
                            </li>
                          </ol>
                        </li>
                        <li>
                          <SlugLink>Customer Connection Point (CCP)</SlugLink>
                        </li>
                      </ol>
                    </li>
                    <li>
                      <SlugLink>EcoFlow Equipment</SlugLink>
                      <ol class="upper-alpha">
                        <li>
                          <SlugLink>Alternator Charger</SlugLink>
                          <ol class="lower-roman">
                            <li>
                              <SlugLink>Output Cables</SlugLink>
                            </li>
                            <li>
                              <SlugLink>Input Cables</SlugLink>
                            </li>
                          </ol>
                        </li>
                        <li>
                          <SlugLink>Power Station</SlugLink>
                        </li>
                      </ol>
                    </li>
                    <li>
                      <SlugLink>Making Connections</SlugLink>
                      <ol class="upper-alpha">
                        <li>
                          <SlugLink>Connect to Battery Cables</SlugLink>
                        </li>
                        <li>
                          <SlugLink>Connect Positive to CCP1</SlugLink>
                        </li>
                        <li>
                          <SlugLink>Connect Positive to CCP2</SlugLink>
                        </li>
                        <li>
                          <SlugLink>Connect Negative to Ground</SlugLink>
                        </li>
                      </ol>
                    </li>
                    <li>
                      <SlugLink>Summary of Steps</SlugLink>
                    </li>
                  </ol>
                </Fragment>
              ),
            },
          ]}
        />
        <Warning prefix="Warning">
          You can get electrocuted by your vehicle's battery and/or CCP. This
          install requires basic electrical knowledge and tools. If you're not
          comfortable working with your vehicle's electrical system, consult a
          qualified professional (feel free to provide them with a link to this
          guide).
        </Warning>
      </Center>
      {/* <Center>
        <p>
          The Ford Transit is a commercial vehicle designed to be upfitted and
          as a result its electrical system contains features not in many
          consumer vehicles. It contains both a Battery Management System (BMS)
          and at least one Customer Connection Point (CCP). Some Transits
          contain two or three CCP connections, each which can have different
          specifications and features. Additionally, many Ford Transits come
          from the factory with dual alternators and/or dual batteries. This
          guide will walk you through selecting which connections are an option
          for your specific vehicle model, Alternator Charger model/cable, and
          power station.
        </p>
      </Center> */}
      <Center>
        <SlugHeading tag="h2">Connection Options</SlugHeading>
        <p>
          An overview of the connection options supported by each model and
          cable combination:
        </p>
        <FixedTable>
          <table>
            <thead>
              <tr>
                <th class={editorialStyle.sticky} />
                <th class={editorialStyle.sticky} />
                <th colSpan={3}>Connection Options</th>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>Model</th>
                <th class={editorialStyle.sticky}>Cable</th>
                <th>Battery</th>
                <th>CCP1</th>
                <th>CCP2</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th class={editorialStyle.sticky} rowspan={3}>
                  <strong>800W</strong>
                </th>
                <th class={editorialStyle.sticky}>XT60i</th>
                <td>
                  <span aria-label="Yes" title="Yes">
                    ✅
                  </span>
                </td>
                <td>
                  <span aria-label="Yes" title="Yes">
                    ✅
                  </span>
                </td>
                <td>
                  <span aria-label="Yes" title="Yes">
                    ✅
                  </span>
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>XT150</strong> <sup>*</sup>
                </th>
                <td>
                  <span aria-label="Yes" title="Yes">
                    ✅
                  </span>
                </td>
                <td>
                  <span aria-label="No" title="No">
                    ❌
                  </span>
                </td>
                <td>
                  <span aria-label="No" title="No">
                    ❌
                  </span>
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>Custom Fit</th>
                <td>
                  <span aria-label="Yes" title="Yes">
                    ✅
                  </span>
                </td>
                <td>
                  <span aria-label="No" title="No">
                    ❌
                  </span>
                </td>
                <td>
                  <span aria-label="No" title="No">
                    ❌
                  </span>
                </td>
              </tr>

              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky} rowspan={3}>
                  <strong>500W</strong>
                </th>
                <th class={editorialStyle.sticky}>
                  <strong>XT60i</strong> <sup>*</sup>
                </th>
                <td>
                  <span aria-label="Yes" title="Yes">
                    ✅
                  </span>
                </td>
                <td>
                  <span aria-label="Yes" title="Yes">
                    ✅
                  </span>
                </td>
                <td>
                  <span aria-label="Yes" title="Yes">
                    ✅
                  </span>
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>XT150</th>
                <td>
                  <span aria-label="Yes" title="Yes">
                    ✅
                  </span>
                </td>
                <td>
                  <span aria-label="Yes" title="Yes">
                    ✅
                  </span>
                </td>
                <td>
                  <span aria-label="No" title="No">
                    ❌
                  </span>
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>Custom Fit</th>
                <td>
                  <span aria-label="Yes" title="Yes">
                    ✅
                  </span>
                </td>
                <td>
                  <span aria-label="Yes" title="Yes">
                    ✅
                  </span>
                </td>
                <td>
                  <span aria-label="No" title="No">
                    ❌
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </FixedTable>
        <p class="caption">
          * Included cable: 800W comes with XT150, 500W comes with XT60i.
        </p>

        <p>
          Features and charge rates supported by each model and cable
          combination:
        </p>
        <FixedTable>
          <table>
            <thead>
              <tr>
                <th class={editorialStyle.sticky} />
                <th colspan={2}>800W</th>
                <th colspan={2}>500W</th>
              </tr>
              <tr>
                <th class={editorialStyle.sticky} />
                <th>XT150</th>
                <th>XT60i</th>
                <th>XT150</th>
                <th>XT60i</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Charge</strong>
                </th>
                <td>
                  <NoWrap>100-800W</NoWrap>
                </td>
                <td>500W</td>
                <td>
                  <NoWrap>100-500W</NoWrap>
                </td>
                <td>500W</td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Reverse Charge</strong>
                </th>
                <td>
                  <NoWrap>100-800W</NoWrap>
                </td>
                <td>
                  <span aria-label="No" title="No">
                    ❌
                  </span>
                </td>
                <td>
                  <NoWrap>100-500W</NoWrap>
                </td>
                <td>
                  <span aria-label="No" title="No">
                    ❌
                  </span>
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Battery Maintenance</strong>
                </th>
                <td>100W</td>
                <td>
                  <span aria-label="No" title="No">
                    ❌
                  </span>
                </td>
                <td>100W</td>
                <td>
                  <span aria-label="No" title="No">
                    ❌
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </FixedTable>
      </Center>

      <Center>
        <SlugHeading tag="h2">Transit Electrical System</SlugHeading>
        <p>
          For additional details on the Ford Transit’s electrical system, refer
          to the BEMM:
        </p>
        <Accordion
          headingsAs="h3"
          items={[
            {
              heading:
                "Download Body and Equipment Mounting Manual (BEMM) for your Transit",
              content: (
                <Fragment>
                  <p>
                    You can locate the BEMM specific to your Transit model
                    online at{" "}
                    <Link
                      external
                      href="https://www.fordpro.com/en-us/upfit/bbas/"
                    >
                      Ford Pro Body Builder Advisory Service (BBAS)
                    </Link>
                    . Some older models can be more difficult to locate.
                  </p>
                  <p>Here are some for quick reference (North America):</p>
                  <ul>
                    {/* <li>
                      <Link
                        external
                        href="https://api-prd.fordpro.com/pro/bbas/download/BBLB/67c1e1bdfa78ad3df02407a7"
                      >
                        2025
                      </Link>
                    </li> */}
                    <li>
                      <Link
                        external
                        href="https://api-prd.fordpro.com/pro/bbas/download/BBLB/66841026c7fa557b06a5d5d8"
                      >
                        2024
                      </Link>
                    </li>
                    <li>
                      <Link
                        external
                        href="https://api-prd.fordpro.com/pro/bbas/download/BBLB/6632290fec530f3fb24e9d28"
                      >
                        2023
                      </Link>
                    </li>
                    <li>
                      <Link
                        external
                        href="https://api-prd.fordpro.com/pro/bbas/download/BBLB/654dc61fbd2cfc7167ebce4f"
                      >
                        2022
                      </Link>
                    </li>

                    <li>
                      <Link
                        external
                        href="https://api-prd.fordpro.com/pro/bbas/download/BBLB/65f096cacab88342d475f33c"
                      >
                        2020-21
                      </Link>
                    </li>
                    <li>
                      <Link
                        external
                        href="https://api-prd.fordpro.com/pro/bbas/download/BBLB/654dc61fbd2cfc7167ebce14"
                      >
                        2020
                      </Link>
                    </li>
                  </ul>
                </Fragment>
              ),
            },
          ]}
        />

        <div class={style.section}>
          <SlugHeading tag="h3">Alternator</SlugHeading>
          <p>
            The current Transit now comes standard with a robust{" "}
            <strong>250A alternator</strong>. These extra amps are ideal for
            charging a power station without putting too much strain on your
            vehicle's alternator. Additionally, some Transits come equipped with
            dual 250A alternators. It’s best to check what your vehicle has
            before pulling the full 800W from an EcoFlow Alternator Charger.
            Most alternators have a sticker with their specifications; however,
            this can sometimes be difficult to see once installed (I can spot
            mine if I lay under my van at just the right angle). Your local Ford
            dealer should be able to help you determine which alternator is
            installed in your van.
          </p>
          <p>
            Recent Transits come with <strong>smart alternators</strong> that
            adjust voltage output based on driving conditions to improve fuel
            efficiency. While this sounds great, it can cause the Alternator
            Charger to stop charging while driving. Even worse, when paired with
            the <strong>XT60i cable</strong> the Alternator Charger will{" "}
            <strong>turn off after 30s of detected inactivity</strong>.
          </p>
        </div>

        <div class={style.section}>
          <SlugHeading tag="h3">Vehicle Battery</SlugHeading>
          <p>
            Unlike most vehicles, the Ford Transit's battery/batteries are
            located inside the cabin under the driver's seat. This is important
            for planning your wire routing.
          </p>
          <p>
            The Transit has 3 battery configurations (each uses a size H6
            battery):
          </p>
          <ol class="upper-alpha">
            <li>
              <strong>Single Battery:</strong> 610CCA
            </li>
            <li>
              <strong>Single AGM Battery:</strong> 760CCA
            </li>
            <li>
              <strong>Dual AGM Batteries:</strong> 760CCA each
            </li>
          </ol>

          <ImageSet
            size={3}
            border={true}
            showAlt
            images={[
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/battery-config-a.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/battery-config-a.webp",
                alt: "A. Single Battery",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/battery-config-b.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/battery-config-b.webp",
                alt: "A. Single AGM Battery",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/battery-config-c.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/battery-config-c.webp",
                alt: "A. Dual AGM Batteries",
              },
            ]}
          />
          <p>
            Two batteries can make for a tighter install of the fuse block when
            choosing to connect to the battery instead of the CCP, but it is
            still manageable. Dual batteries can act as a bit of an extra buffer
            to the alternator and also mean you don't need to worry as much if
            you accidentally leave your headlights on.
          </p>

          <SlugHeading tag="h4">Battery Management System (BMS)</SlugHeading>
          <p>
            The Battery Management System (BMS) is responsible for monitoring
            and managing the battery's condition, charging rate, and power
            distribution. It ensures that the battery maintains an optimal
            charge while preventing excessive drain from vehicle accessories.
            When connecting directly to the battery cables, it is important not
            to bypass the BMS sensors.
          </p>
        </div>

        <div class={style.section}>
          <SlugHeading tag="h3">Customer Connection Point (CCP)</SlugHeading>

          <p>
            To determine which Customer Connection Points (CCPs) are installed
            in your Transit, remove the CCP cover (located on the outside edge
            of your driver's seat):
          </p>
          <ul>
            <li>
              <strong>One Stud:</strong> only CCP1 is present.
            </li>
            <li>
              <strong>Two Studs:</strong> CCP1 and CCP2 are present.
            </li>
            <li>
              <strong>Three Studs:</strong> Three CCP connections are present
              (each with the same spec as CCP1, follow guide for CCP1).
            </li>
          </ul>

          <ImageSet
            size={3}
            border={true}
            showAlt
            images={[
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/ccp-1-only.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/ccp-1-only.webp",

                alt: "CCP1",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/ccp.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/ccp.webp",

                alt: "CCP1 and CCP2",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/ccp-3x.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/ccp-3x.webp",

                alt: "3x CCP",
              },
            ]}
          />

          <p style="clear: both;" />

          <FixedTable>
            <table>
              <thead>
                <tr>
                  <th class={editorialStyle.sticky} />
                  <th>Fused Amps</th>
                  <th>Ignition Controlled</th>
                  <th>Torque</th>
                  <th>Nut</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>CCP1</strong>
                  </th>
                  <td>60A</td>
                  <td>No</td>
                  <td>
                    <NoWrap>2.6-3.6 ft·lb</NoWrap> <NoWrap>(3.5-4.5Nm)</NoWrap>
                  </td>
                  <td>M5</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>CCP2</strong>
                  </th>
                  <td>175A</td>
                  <td>Yes</td>
                  <td>
                    <NoWrap>8.85 ft·lb</NoWrap> <NoWrap>(12Nm)</NoWrap>
                  </td>
                  <td>M8</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>3x CCP</strong>
                  </th>
                  <td>60A each</td>
                  <td>No</td>
                  <td>
                    <NoWrap>2.6-3.6 ft·lb</NoWrap> <NoWrap>(3.5-4.5Nm)</NoWrap>
                  </td>
                  <td>M5</td>
                </tr>
              </tbody>
            </table>
          </FixedTable>

          <Warning prefix="Caution">
            Use caution when removing the CCP cover as the seat base can ground
            to the battery. When connecting to the CCP, it is recommended to
            disconnect the battery ground so as to avoid a short circuit.
          </Warning>
        </div>
      </Center>
      <Center>
        <SlugHeading tag="h2">EcoFlow Equipment</SlugHeading>
        <p>
          If you need help determining which EcoFlow Alternator Charger or cable
          is best for you, please see one of my reviews:{" "}
          <Link href={namedRoutes.ecoflow.altCharger.quick}>quick review</Link>{" "}
          or{" "}
          <Link href={namedRoutes.ecoflow.altCharger.review}>
            in-depth review
          </Link>
          .
        </p>

        <div class={style.section}>
          <SlugHeading tag="h3">Alternator Charger</SlugHeading>
          <p>There are currently two models available:</p>
          <ul class="none">
            <li>
              <PhotoListItem
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/800w-alternator-charger"
                img="/images/products/ecoflow/alternator-charger-800w/front-right.webp"
                title="800W Alternator Charger"
                description={<Fragment>Included Cable: XT150</Fragment>}
                buttonText="EcoFlow"
                buttonIcon="EcoFlow"
              />
            </li>
            <li>
              <PhotoListItem
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/ecoflow-500w-alternator-charger"
                img="/images/products/ecoflow/alternator-charger-500w/front.webp"
                title="500W Alternator Charger"
                description={<Fragment>Included Cable: XT60i</Fragment>}
                buttonText="EcoFlow"
                buttonIcon="EcoFlow"
              />
            </li>
          </ul>
        </div>

        <div class={style.section}>
          <SlugHeading tag="h4">Output Cables </SlugHeading>
          <p>
            The output cable connects the Alternator Charger to your power
            station. There are currently three types of cables available. Two of
            these come included with different units, but all three can be
            purchased separately:
          </p>
          <ul class="none">
            <li>
              <PhotoListItem
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/ecoflow-alternator-charger-xt60-output-cable-1m"
                img="/images/products/ecoflow/alternator-charger-500w/output-cable-xt60i.webp"
                title="XT60i Output Cable"
                description={
                  <Fragment>
                    Length: 1m <br />
                    (included with 500W model, supports some 3rd-party power
                    stations)
                  </Fragment>
                }
                buttonText="EcoFlow"
                buttonIcon="EcoFlow"
              />
            </li>
            <li>
              <PhotoListItem
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/aecoflow-alternator-charger-xt150-output-cable-2m"
                img="/images/products/ecoflow/alternator-charger-800w/output-cable-xt150-2m.webp"
                title="XT150 Output Cable"
                description={
                  <Fragment>
                    Length: 2m <br />
                    (included with 800W model)
                  </Fragment>
                }
                buttonText="EcoFlow"
                buttonIcon="EcoFlow"
              />
            </li>
            <li>
              <PhotoListItem
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/ecoflow-alternator-charger-custom-fit-output-cable-2m"
                img="/images/products/ecoflow/accessories/output-cable-custom-fit-2m.webp"
                title="Custom Fit Output Cable"
                description={
                  <Fragment>
                    Length: 2m <br />
                    (connects to extra battery port on Delta Pro)
                  </Fragment>
                }
                buttonText="EcoFlow"
                buttonIcon="EcoFlow"
              />
            </li>
          </ul>
        </div>

        <div class={style.section}>
          <SlugHeading tag="h4">Input Cables</SlugHeading>
          <p>
            The input cable connects the Alternator Charger to your vehicle's
            electrical system and comes included with the unit. The 800W model
            comes with 6AWG and the 500W model comes with 8AWG.
          </p>
          <ImageSet
            shadow={true}
            showAlt
            images={[
              {
                max: "/images/r/ecoflow-alternator-chargers/max/20250425_233552515.jpg",
                lg: "/images/r/ecoflow-alternator-chargers/lg/20250425_233552515.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/20250425_233552515.webp",
                alt: "EcoFlow Alternator Charger 8AWG vs 6AWG cable comparison",
              },
              {
                max: "/images/r/ecoflow-alternator-chargers/max/20250425_233501405.jpg",
                lg: "/images/r/ecoflow-alternator-chargers/lg/20250425_233501405.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/20250425_233501405.webp",
                alt: "EcoFlow Alternator Charger cable and fuse block comparison",
              },
            ]}
          />

          <Accordion
            headingsAs="h5"
            items={[
              {
                heading: "Extending the Input Cables",
                content: (
                  <Fragment>
                    <p>
                      <strong>Uncommon:</strong> If you need to extend the input
                      cable, consider using a larger wire gauge (
                      <Link
                        external
                        href="https://www.bluesea.com/support/articles/Circuit_Protection/1437/Part_1%3A_Choosing_the_Correct_Wire_Size_for_a_DC_Circuit"
                      >
                        choosing the correct wire size
                      </Link>
                      ) or purchase EcoFlow's{" "}
                      <Link
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/ecoflow-alternator-charger-extended-fuse-cable-2m"
                      >
                        Extended Fuse Cable
                      </Link>
                      :
                    </p>
                    <PhotoListItem
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/ecoflow-alternator-charger-extended-fuse-cable-2m"
                      img="/images/products/ecoflow/accessories/extended-fuse-cable-2m.webp"
                      title="Extended Fuse Cable"
                      description={<Fragment> Length: 2m</Fragment>}
                      buttonText="EcoFlow"
                      buttonIcon="EcoFlow"
                    />
                    <p>
                      There is a setting in the EcoFlow app when you need to
                      extend the positive electrode, however it doesn't offer
                      any such setting for the negative electrode.
                    </p>
                    <ImageSet
                      size={4}
                      images={[
                        {
                          max: "/images/r/ecoflow-alternator-chargers/max/20250426_003707000.png",
                          lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_003707000.webp",
                          sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_003707000.webp",
                          alt: "Screenshot of EcoFlow app",
                        },
                        {
                          max: "/images/r/ecoflow-alternator-chargers/max/20250426_003227000.png",
                          lg: "/images/r/ecoflow-alternator-chargers/lg/20250426_003227000.webp",
                          sm: "/images/r/ecoflow-alternator-chargers/sm/20250426_003227000.webp",
                          alt: "Screenshot of EcoFlow app",
                        },
                      ]}
                    />
                  </Fragment>
                ),
              },
            ]}
          />
        </div>

        <div class={style.section}>
          <SlugHeading tag="h3">Power Station</SlugHeading>
          <p>
            The power station needs to have one of the following connections:
          </p>
          <ul>
            <li>XT150</li>
            <li>XT60i (also support 3rd-party power stations)</li>
            <li>Delta Pro 4+8 Extra Battery Port</li>
          </ul>
          <Accordion
            headingsAs="h4"
            items={[
              {
                heading: "Delta Pro 4+8 Extra Battery Port",
                content: (
                  <Fragment>
                    <p>
                      Either use XT150 cable with the{" "}
                      <Link
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/delta-pro-to-smart-generator-adapter"
                      >
                        Smart Generator Adapter
                      </Link>{" "}
                      or the{" "}
                      <Link
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/ecoflow-alternator-charger-custom-fit-output-cable-2m"
                      >
                        Custom Fit Output Cable
                      </Link>
                      .
                    </p>
                    <ImageSet
                      size={3}
                      shadow={true}
                      showAlt
                      images={[
                        {
                          max: "/images/r/ecoflow-alternator-chargers/max/20250427_151257122.jpg",
                          lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_151257122.webp",
                          sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_151257122.webp",
                          alt: "EcoFlow Smart Generator Adapter and XT150 cable",
                        },
                        {
                          max: "/images/r/ecoflow-alternator-chargers/max/20250427_151202560.jpg",
                          lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_151202560.webp",
                          sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_151202560.webp",
                          alt: "EcoFlow Smart Generator Adapter and XT150 cable",
                        },
                        {
                          max: "/images/r/ecoflow-alternator-chargers/max/20250427_182938967.jpg",
                          lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_182938967.webp",
                          sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_182938967.webp",
                          alt: "XT150 and XT60i connected to EcoFlow Delta Pro",
                        },
                      ]}
                    />
                  </Fragment>
                ),
              },
            ]}
          />
        </div>
      </Center>
      <Center>
        <SlugHeading tag="h2">Making Connections</SlugHeading>
        <p>
          You will either need to connect to the Transit's battery cables or to
          CCP1 or CCP2.
        </p>

        <div class={style.section}>
          <SlugHeading tag="h3">Connect to Battery Cables</SlugHeading>
          <p>
            <strong>Recommend:</strong> This connection works with both models
            and has the least restrictions. It was recommended to me by a Ford
            Pro BBAS Services Engineer after I explained the EcoFlow Alternator
            Charger's specifications and functions.
          </p>
          <p>
            Here is how mine is connected. Note I also upgraded the fuse block
            (optional) as the original fuse block had issues.
          </p>
          <ImageSet
            shadow={true}
            showAlt
            images={[
              {
                max: "/images/r/ecoflow-alternator-chargers/max/20240720_221759375.jpg",
                lg: "/images/r/ecoflow-alternator-chargers/lg/20240720_221759375.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/20240720_221759375.webp",
                alt: "Ford Transit dual batteries with EcoFlow alternator charger cables and Blue Sea fuse block",
              },
              {
                max: "/images/r/ecoflow-alternator-chargers/max/20240720_221759375_install.png",
                lg: "/images/r/ecoflow-alternator-chargers/lg/20240720_221759375_install.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/20240720_221759375_install.webp",
                alt: "Highlighted connections for Ford Transit dual batteries with EcoFlow alternator charger cables and Blue Sea fuse block",
              },
              {
                max: "/images/r/ecoflow-alternator-chargers/max/20240720_223337589.jpg",
                lg: "/images/r/ecoflow-alternator-chargers/lg/20240720_223337589.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/20240720_223337589.webp",
                alt: "Ford Transit dual batteries with EcoFlow alternator charger cables",
              },
              {
                max: "/images/r/ecoflow-alternator-chargers/max/20240720_223337589_install.png",
                lg: "/images/r/ecoflow-alternator-chargers/lg/20240720_223337589_install.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/20240720_223337589_install.webp",
                alt: "Highlighted connections for Ford Transit dual batteries with EcoFlow alternator charger",
              },
            ]}
          />

          <Accordion
            headingsAs="h4"
            items={[
              {
                heading: "Fuse Block (old vs new design)",
                content: (
                  <Fragment>
                    <p>
                      The old fuse block design was flawed; if too much strain
                      was put on the cable it would cause the fuse to break
                      which would result in an under voltage error and the
                      inability to charge anything with the unit. I encountered
                      this on my 800W model (I’m unsure which fuse block the
                      current 800W model ships with currently as I preordered
                      mine last year when it was announced).
                    </p>
                    <ImageSet
                      size={4}
                      shadow={true}
                      showAlt
                      images={[
                        {
                          max: "/images/r/ecoflow-alternator-chargers/max/20250505_184511737.jpg",
                          lg: "/images/r/ecoflow-alternator-chargers/lg/20250505_184511737.webp",
                          sm: "/images/r/ecoflow-alternator-chargers/sm/20250505_184511737.webp",
                          alt: "Fuse blocks and 6AWG vs 8AWG cables",
                        },
                        {
                          max: "/images/r/ecoflow-alternator-chargers/max/20240807_032740321.jpg",
                          lg: "/images/r/ecoflow-alternator-chargers/lg/20240807_032740321.webp",
                          sm: "/images/r/ecoflow-alternator-chargers/sm/20240807_032740321.webp",
                          alt: "Blue Sea fuse block mounted in Ford Transit",
                        },
                        {
                          max: "/images/r/ecoflow-alternator-chargers/max/20240719_210422414.jpg",
                          lg: "/images/r/ecoflow-alternator-chargers/lg/20240719_210422414.webp",
                          sm: "/images/r/ecoflow-alternator-chargers/sm/20240719_210422414.webp",
                          alt: "EcoFlow Alternator Charger vs Blue Sea Systems fuse block comparison",
                        },
                        {
                          max: "/images/r/ecoflow-alternator-chargers/max/20250427_145200253.jpg",
                          lg: "/images/r/ecoflow-alternator-chargers/lg/20250427_145200253.webp",
                          sm: "/images/r/ecoflow-alternator-chargers/sm/20250427_145200253.webp",
                          alt: "EcoFlow AEP fuses: new vs broken",
                        },
                      ]}
                    />
                    <p>
                      To resolve my issue, I purchased a{" "}
                      <Link
                        affiliate="https://amzn.to/3JVdHAb"
                        href="https://www.amazon.com/dp/B004T1VYLA"
                      >
                        Blue Sea 7720 Safety Fuse Block
                      </Link>{" "}
                      which has separate posts for the fuse ends and the cables.
                      The new design incorporates this separate post design.
                      This helps prevent the issue where lateral strain on the
                      cable would cause the fuse to shift and break.
                    </p>
                  </Fragment>
                ),
              },
            ]}
          />

          <p>
            Here are the connection points for each of the three battery
            configurations:
          </p>
          <ImageSet
            size={3}
            border={true}
            showAlt
            images={[
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/battery-config-a.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/battery-config-a.webp",
                alt: "A. Single Battery",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/battery-config-b.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/battery-config-b.webp",
                alt: "A. Single AGM Battery",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/battery-config-c.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/battery-config-c.webp",
                alt: "A. Dual AGM Batteries",
              },
            ]}
          />

          <Accordion
            headingsAs="h4"
            items={[
              {
                heading: "Accessing the Batteries",
                content: (
                  <Fragment>
                    <p>
                      Watch the following video by Ford on how to access the
                      battery cables. If needed, you can remove the driver's
                      seat (instructions below).
                    </p>
                    <YouTube id="xglmBiyTifQ" time="42" />

                    <p>
                      The battery cables should be fixed to the terminal
                      positive and negative posts with a torque of 8.0 Nm ± 1.2
                      Nm.
                    </p>
                  </Fragment>
                ),
              },
              {
                heading: "Removing the Driver's Seat",
                content: (
                  <Fragment>
                    <p>
                      Removing the driver's seat may be necessary, depending on
                      how you plan to route the cables and mount the fuse block.
                      Here is a video demonstrating the process, though it may
                      vary slightly depending on your seat setup. While the
                      video includes seat belt removal, this step isn’t
                      required—you can simply set the seat aside, either inside
                      or outside the van.
                    </p>
                    <YouTube id="8GWCQiwQZ2g" time="105" />
                  </Fragment>
                ),
              },
            ]}
          />
        </div>

        <div class={style.section}>
          <SlugHeading tag="h3">Connect Positive to CCP1</SlugHeading>
          <p>
            <strong>Only supported for the 500W model</strong> (or 800W model
            using XT60i cable). Since CCP1 is fused at 60A, it doesn’t support
            the 76A maximum charge rating of the 800W model (the 500W model's
            maximum charge rating is 45A). CCP1 does support reverse charge and
            battery maintenance features since it isn't ignition controlled.
            Uses an M5 nut torqued to <NoWrap>2.6-3.6 ft·lb</NoWrap>{" "}
            <NoWrap>(3.5-4.5Nm)</NoWrap>.
          </p>
          <ImageSet
            shadow={true}
            showAlt
            images={[
              {
                max: "/images/r/ecoflow-alternator-chargers/max/20250514_020942584.jpg",
                lg: "/images/r/ecoflow-alternator-chargers/lg/20250514_020942584.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/20250514_020942584.webp",
                alt: "Ford Transit CCP1 with EcoFlow 8AWG cable",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/ccp-1.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/ccp-1.webp",
                border: true,
                alt: "CCP1",
              },
              {
                max: "/images/r/ecoflow-alternator-chargers/max/20250514_021611049.jpg",
                lg: "/images/r/ecoflow-alternator-chargers/lg/20250514_021611049.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/20250514_021611049.webp",
                alt: "Ford Transit CCP with cover",
              },
            ]}
          />
          <Warning prefix="Caution">
            Use caution when removing the CCP cover as the seat base can ground
            to the battery. When connecting to the CCP, it is recommended to
            disconnect the battery ground so as to avoid a short circuit.
          </Warning>
        </div>

        <div class={style.section}>
          <SlugHeading tag="h3">Connect Positive to CCP2</SlugHeading>
          <p>
            <strong>Supported for either model using the XT60i cable.</strong>{" "}
            Doesn't fully support reverse charge or battery maintenance
            features. CCP2 is ignition controlled, meaning it only provides
            power when the van is running (with an automatic shutoff delay).
            This prevents accidental battery drain but also means it won't
            provide charging when the van is off. Because CCP2 is fused at 175A
            it supports the maximum charge ratings of both models (45A and 76A).
            Uses an M8 nut torqued to <NoWrap>8.85 ft·lb</NoWrap>{" "}
            <NoWrap>(12Nm)</NoWrap>.
          </p>
          <ImageSet
            shadow={true}
            showAlt
            images={[
              {
                max: "/images/r/ecoflow-alternator-chargers/max/20250514_021243690.jpg",
                lg: "/images/r/ecoflow-alternator-chargers/lg/20250514_021243690.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/20250514_021243690.webp",
                alt: "Ford Transit CCP2 with EcoFlow 8AWG cable",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/ccp-2.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/ccp-2.webp",
                border: true,
                alt: "CCP2",
              },
            ]}
          />
          <Warning prefix="Caution">
            Use caution when removing the CCP cover as the seat base can ground
            to the battery. When connecting to the CCP, it is recommended to
            disconnect the battery ground so as to avoid a short circuit.
          </Warning>
        </div>

        <div class={style.section}>
          <SlugHeading tag="h3">Connect Negative to Ground</SlugHeading>
          <p>
            You will be connecting the charger's negative cable to either the
            battery's negative cable or to a chassis ground point. The following
            images depict the official chassis ground points and the negative
            terminal connection recommended by a Ford Pro BBAS Services Engineer
            (2024 model year). Ground point <strong>"N"</strong> is a specific
            point on the van's negative battery cable. Ground points{" "}
            <strong>#78</strong> and <strong>#79</strong> are also close by but
            I found they are not easy to access (the floor panels in this spot
            are difficult to remove). Ideally, Ford would have included a
            dedicated ground point under the CCP cover. If none of the existing
            ground points are suitable, you can add a new one by following the
            guidelines outlined in the 2024 BEMM (see{" "}
            <em>Adding a New Ground Point</em> below).
          </p>
          <ImageSet
            shadow={true}
            showAlt
            images={[
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/ground-points-2024-gas.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/ground-points-2024-gas.webp",
                border: true,
                alt: "2024 Ford Transit ground points and negative terminal",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/ground-n.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/ground-n.webp",
                border: true,
                alt: "2024 Ford Transit negative terminal",
              },
              {
                max: "/images/r/ecoflow-alternator-chargers/max/20240719_202430339.jpg",
                lg: "/images/r/ecoflow-alternator-chargers/lg/20240719_202430339.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/20240719_202430339.webp",
                alt: "Photo of Ford Transit negative terminal connection",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/ground-79-78.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/ground-79-78.webp",
                border: true,
                alt: "2024 Ford Transit ground points #78-79",
              },
            ]}
          />
          <Accordion
            headingsAs="h4"
            items={[
              {
                heading: "Accessing the Batteries",
                content: (
                  <Fragment>
                    <p>
                      Watch the following video by Ford on how to access the
                      battery cables. If needed, you can remove the driver's
                      seat (instructions below).
                    </p>
                    <YouTube id="xglmBiyTifQ" time="42" />
                  </Fragment>
                ),
              },
              {
                heading: "Removing the Driver's Seat",
                content: (
                  <Fragment>
                    <p>
                      Removing the driver's seat may be necessary, depending on
                      how you plan to route the cables and mount the fuse block.
                      Here is a video demonstrating the process, though it may
                      vary slightly depending on your seat setup. While the
                      video includes seat belt removal, this step isn’t
                      required—you can simply set the seat aside, either inside
                      or outside the van.
                    </p>
                    <YouTube id="8GWCQiwQZ2g" time="105" />
                  </Fragment>
                ),
              },
              {
                heading: "Adding a New Ground Point",
                content: (
                  <Fragment>
                    <p>
                      If an existing official ground point is unsuitable, you
                      can add a new one by following the instructions in the{" "}
                      <Link
                        external
                        href="https://api-prd.fordpro.com/pro/bbas/download/BBLB/66841026c7fa557b06a5d5d8"
                      >
                        2024 BEMM
                      </Link>
                      :
                    </p>
                    <blockquote>
                      <strong>4.2.5 Grounding</strong>
                      <br />
                      <p>
                        If a new grounding point is required, avoid placing it
                        in a wet area, especially for high current grounds.
                        Ground connections should be routed back close to the
                        location of the +12V supply. This helps to reduce the
                        electromagnetic field particularly generated by inrush
                        current and improve electromagnetic compatibility.
                      </p>
                      <p>
                        Drill point screws are not to be used for any ground
                        attachments:
                      </p>
                      <ul>
                        <li>
                          Do not ground to moving structures, for example:
                          doors, deck lids, lift gates, as the ground return
                          path through the hinges is not reliable
                        </li>
                        <li>
                          Do not place more than 2 eyelet terminals under a
                          single ground screw Refer to: 4.5 Battery and Cables
                        </li>
                        <li>
                          Do not place electrical component attachments or
                          ground nuts adjacent to vehicle fuel tanks or fuel
                          lines
                        </li>
                        <li>
                          Each individual third party load must have a dedicated
                          ground connected directly to the vehicle body ground
                          or to the battery negative spare stud - see
                          ‘Additional Loads and Charging Systems’ later in this
                          section of the BEMM
                        </li>
                        <li>
                          Multiple grounds must not go to an interim splice to a
                          main ground cable
                        </li>
                        <li>
                          If additional ground studs are required it is
                          recommended to install a busbar with multiple studs
                          fed directly from the battery ground post. See
                          ‘Additional Loads and Charging Systems’
                        </li>
                      </ul>
                    </blockquote>
                    <p class="caption">
                      Thanks to FarOutRide for bringing this to my attention on
                      Facebook.
                    </p>
                  </Fragment>
                ),
              },
            ]}
          />
        </div>
      </Center>
      <Center>
        <SlugHeading tag="h2">Summary of Steps</SlugHeading>
        <p>
          Before beginning the final cable routing, it's a good idea to dry fit
          the components to ensure everything works as expected. This allows you
          to test connections and verify functionality before committing to
          permanent installation.
        </p>
        <ol>
          <li>
            <strong>Route cables.</strong> Depending on your build, cables may
            need to be routed under the floor, through walls, or behind
            cabinets. Completing this step first is often best, as the end that
            connects to the van is the smallest. This makes it easier to pull
            through tight spaces where the larger end may not fit.
          </li>
          <li>
            <strong>Connect input cable to van's electrical system.</strong>{" "}
            Securely connect the positive cable to either the battery cable,
            CCP1, or CCP2. Connect the negative cable to either the battery
            cable or a suitable ground point.
          </li>
          <li>
            <strong>Mount EcoFlow Alternator Charger.</strong> Attach it
            securely using screws or bolts. If needed, it can temporarily rest
            on the floor during setup.
          </li>
          <li>
            <strong>
              Connect input/output cables to EcoFlow Alternator Charger.
            </strong>{" "}
            If you need to remove either cable, keep the included removal tool
            handy.
          </li>
          <li>
            <strong>Connect output cable to power station.</strong> Connect to
            either the XT150 or XT60i port on your power station. If using a
            Delta Pro, connect to the Extra Battery port via the{" "}
            <Link
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/delta-pro-to-smart-generator-adapter"
            >
              Smart Generator Adapter
            </Link>{" "}
            or the{" "}
            <Link
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/ecoflow-alternator-charger-custom-fit-output-cable-2m"
            >
              Custom Fit Output Cable
            </Link>
            .
          </li>
          <li>
            <strong>Press power button on Alternator Charger.</strong> Locate
            the small black button on the side of the COM port and press it to
            power the unit on.
          </li>
          <li>
            <strong>Set start voltage in EcoFlow app.</strong> Adjust this
            setting to ensure the Alternator Charger automatically starts and
            stops based on whether the vehicle is on or off.
          </li>
        </ol>

        <EcoFlowCouponCode
          href="https://us.ecoflow.com/"
          buttonText="EcoFlow Store"
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Where to Buy</SlugHeading>

        <ButtonDualGroup>
          <ButtonGroup title="800W Alternator Charger">
            <ButtonList>
              <Button
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/800w-alternator-charger"
                icon="EcoFlow"
              >
                Buy from EcoFlow
              </Button>
              <Button
                affiliate="https://amzn.to/3N4b6p1"
                href="https://www.amazon.com/dp/B0DSBZVPS6/"
                secondary
                icon="Amazon"
              >
                Buy on Amazon
              </Button>
            </ButtonList>
          </ButtonGroup>
          <ButtonGroup title="500W Alternator Charger">
            <ButtonList>
              <Button
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/ecoflow-500w-alternator-charger"
                icon="EcoFlow"
              >
                Buy from EcoFlow
              </Button>
              <Button
                affiliate="https://amzn.to/4jzlwsP"
                href="https://www.amazon.com/dp/B0FYCJ6KD3/"
                secondary
                icon="Amazon"
              >
                Buy on Amazon
              </Button>
            </ButtonList>
          </ButtonGroup>
        </ButtonDualGroup>

        <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
      </Center>
      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.ecoflow.vanlife} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.wiring12v} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.review} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.gen2} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.inventory.vanlife} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <div style="font-size: 0.7em;">
          <p>
            * I'm not an electrical engineer or electrician and take no
            responsibility for issues which occur as a result of this
            information.
          </p>
          <p>
            ** I was not paid for this install guide, but did recently receive a
            free 500W Alternator Charger as part of an EcoFlow beta program.
          </p>
        </div>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
      <StickyButtons
        title="EcoFlow Alternator Chargers"
        description="800W and 500W"
        options={[
          {
            text: "View on EcoFlow",
            icon: "EcoFlow",
            options: [
              {
                text: "800W Alternator Charger",
                affiliateData: affiliateMerchants.ecoflow,
                href: "https://us.ecoflow.com/products/800w-alternator-charger",
              },
              {
                text: "500W Alternator Charger",
                affiliateData: affiliateMerchants.ecoflow,
                href: "https://us.ecoflow.com/products/ecoflow-500w-alternator-charger",
              },
            ],
          },
          {
            text: "View on Amazon",
            icon: "Amazon",
            secondary: true,
            options: [
              {
                text: "800W Alternator Charger",
                affiliate: "https://amzn.to/3N4b6p1",
                href: "https://www.amazon.com/dp/B0DSBZVPS6/",
              },
              {
                text: "500W Alternator Charger",
                affiliate: "https://amzn.to/4jzlwsP",
                href: "https://www.amazon.com/dp/B0FYCJ6KD3/",
              },
            ],
          },
        ]}
      />
    </Editorial>
  </div>
);

export default Page;
